<template>
  <div class="card shadow-sm table-card mb-4">
    <div class="form-card-header flex-wrap">
      <div class="section-number"><i class="fe fe-layers" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">Diagnosis &amp; service analysis</h6>
        <small style="color: rgba(255,255,255,.75);">Claims, amounts claimed, approved, pending and rejected. Click a row to see its facilities, then a facility to see the claims.</small>
      </div>
      <div class="btn-group btn-group-sm" role="group" aria-label="Analyse by">
        <button v-for="d in dimensions" :key="d.key" type="button" class="btn"
          :class="dimension === d.key ? 'btn-light' : 'btn-outline-light'" @click="setDimension(d.key)">{{ d.label }}</button>
      </div>
    </div>
    <div class="card-body">
      <!-- Breadcrumb: item → facility → claims -->
      <div class="d-flex flex-wrap align-items-center mb-3" style="gap: 8px;">
        <nav aria-label="Drill-down" class="crumbs">
          <a href="#" @click.prevent="goTo('items')" :class="{ active: level === 'items' }">All {{ currentDimension.plural }}</a>
          <template v-if="item">
            <i class="fe fe-chevron-right"></i>
            <a href="#" @click.prevent="goTo('facilities')" :class="{ active: level === 'facilities' }">{{ item.name }}</a>
          </template>
          <template v-if="facility">
            <i class="fe fe-chevron-right"></i>
            <span class="active">{{ facility.name }}</span>
          </template>
        </nav>
        <template v-if="level !== 'claims'">
          <label class="small text-muted mb-0 ml-md-auto">Rank by</label>
          <select class="form-control form-control-sm" style="width: auto;" v-model="sort" @change="load">
            <option value="claimed">Amount claimed</option>
            <option value="approved">Amount approved</option>
            <option value="pending">Amount pending</option>
            <option value="rejected">Amount rejected</option>
            <option value="claims">Number of claims</option>
          </select>
          <select v-if="level === 'items'" class="form-control form-control-sm" style="width: auto;" v-model.number="limit" @change="load" aria-label="How many">
            <option :value="20">Top 20</option><option :value="50">Top 50</option><option :value="100">Top 100</option><option :value="500">Top 500</option>
          </select>
        </template>
        <button class="btn btn-sm btn-outline-success" :class="{ 'ml-auto': level === 'claims' }" @click="exportCsv" :disabled="exporting">
          <i class="fe fe-download mr-1"></i> {{ exporting ? "Exporting…" : "Export CSV" }}
        </button>
      </div>

      <div v-if="loading && !data" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
      <div v-else-if="error" class="empty-state"><i class="fe fe-alert-triangle"></i>{{ error }}</div>
      <template v-else-if="data">
        <!-- Totals of the current level -->
        <div v-if="data.totals" class="d-flex flex-wrap mb-3" style="gap: 22px;">
          <div><span class="stat-label">Claims</span><div class="stat-value">{{ num(data.totals.claims) }}</div></div>
          <div><span class="stat-label">Claimed</span><div class="stat-value">{{ money(data.totals.amount_claimed) }}</div></div>
          <div><span class="stat-label">Approved</span><div class="stat-value text-success">{{ money(data.totals.amount_approved) }}</div></div>
          <div><span class="stat-label">Pending</span><div class="stat-value text-warning">{{ money(data.totals.amount_pending) }}</div></div>
          <div><span class="stat-label">Rejected</span><div class="stat-value text-danger">{{ money(data.totals.amount_rejected) }}</div></div>
          <div v-if="level === 'items'"><span class="stat-label">Facilities</span><div class="stat-value">{{ num(data.totals.facilities) }}</div></div>
        </div>

        <!-- Items and facilities -->
        <template v-if="level !== 'claims'">
          <div v-if="!data.rows.length" class="empty-state">Nothing was claimed for the filters above.</div>
          <template v-else>
            <apexchart v-if="level === 'items'" type="bar" :height="Math.max(260, chartRows.length * 30)" :options="chartOptions" :series="chartSeries" :key="chartKey" />
            <div class="table-responsive mt-2">
              <table class="table table-sm mb-0">
                <thead class="table-head">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">{{ level === 'items' ? currentDimension.singular : 'Facility' }}</th>
                    <th v-if="level === 'facilities'" class="table-th">Type</th>
                    <th v-if="level === 'facilities'" class="table-th">LGA</th>
                    <th v-if="level === 'items'" class="table-th text-right">Facilities</th>
                    <th class="table-th text-right" title="Approved / pending / rejected claims">Claims</th>
                    <th class="table-th text-right">Claimed</th>
                    <th class="table-th text-right">Approved</th>
                    <th class="table-th text-right">Pending</th>
                    <th class="table-th text-right">Rejected</th>
                    <th class="table-th text-right">Share</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="table-row clickable" v-for="(r, i) in data.rows" :key="(r.key || r.facility_id || 'x') + '-' + i" @click="drill(r)"
                    :title="level === 'items' ? 'See the facilities' : 'See the claims'">
                    <td class="text-muted">{{ i + 1 }}</td>
                    <td>
                      <b>{{ level === 'items' ? r.name : r.facility }}</b>
                      <div class="small text-muted" v-if="level === 'items' && r.code">{{ r.code }}</div>
                    </td>
                    <td v-if="level === 'facilities'" class="small">{{ [r.phc_type, r.phc_general].filter(Boolean).join(" · ") || "—" }}</td>
                    <td v-if="level === 'facilities'" class="small">{{ r.lga || "—" }}</td>
                    <td v-if="level === 'items'" class="text-right">{{ num(r.facilities) }}</td>
                    <td class="text-right">
                      <b>{{ num(r.claims) }}</b>
                      <div class="small text-nowrap">
                        <span class="text-success" title="Approved">{{ num(r.approved_count) }}</span> /
                        <span class="text-warning" title="Pending">{{ num(r.pending_count) }}</span> /
                        <span class="text-danger" title="Rejected">{{ num(r.rejected_count) }}</span>
                      </div>
                    </td>
                    <td class="text-right">{{ money(r.amount_claimed) }}</td>
                    <td class="text-right text-success">{{ money(r.amount_approved) }}</td>
                    <td class="text-right text-warning">{{ money(r.amount_pending) }}</td>
                    <td class="text-right text-danger">
                      {{ money(r.amount_rejected) }}
                      <div v-if="r.rejected_lines" class="small text-muted">{{ num(r.rejected_lines) }} item(s) rejected</div>
                    </td>
                    <td class="text-right">{{ r.share_pct === null ? "—" : r.share_pct + "%" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="small text-muted mt-2 mb-0">
              Claims: approved / pending / rejected. Pending covers new, vetted, verified, forwarded and sent-back claims.
              Rejected amounts include items rejected on their own in claims that were otherwise accepted.
              <template v-if="dimension === 'diagnosis'">A claim is counted under its main diagnosis.</template>
            </p>
          </template>
        </template>

        <!-- Claims of one facility -->
        <template v-else>
          <div v-if="!data.rows.length" class="empty-state">No claims.</div>
          <div v-else class="table-responsive">
            <table class="table table-sm mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">Claim</th><th class="table-th">Date</th><th class="table-th">Patient</th><th class="table-th">Programme</th>
                  <th v-if="dimension !== 'diagnosis'" class="table-th">Diagnosis</th>
                  <th class="table-th">Status</th><th class="table-th text-right">Claimed</th><th class="table-th text-right">Approved</th><th class="table-th"></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="c in data.rows">
                  <tr class="table-row" :key="'c' + c.id">
                    <td><router-link :to="'/claim/' + c.id" target="_blank">{{ c.claim_unique_id || '#' + c.id }}</router-link></td>
                    <td class="small">{{ c.date }}</td>
                    <td class="small">{{ c.patient || "—" }}<div class="text-muted">{{ c.id_card_number }}</div></td>
                    <td class="small">{{ c.programme || "—" }}</td>
                    <td v-if="dimension !== 'diagnosis'" class="small">{{ c.diagnosis || "—" }}</td>
                    <td><span class="badge" :class="statusClass(c.status)">{{ statusLabel(c.status) }}</span></td>
                    <td class="text-right">{{ money(c.amount_claimed) }}</td>
                    <td class="text-right">{{ money(c.amount_approved) }}</td>
                    <td class="text-right">
                      <button class="btn btn-link btn-sm p-0" @click="toggle(c.id)">
                        {{ open[c.id] ? "Hide" : (dimension === 'service' || dimension === 'drug' ? "Lines" : "Services") }} ({{ c.items.length }})
                      </button>
                    </td>
                  </tr>
                  <tr v-if="open[c.id]" :key="'i' + c.id" class="item-row">
                    <td colspan="9">
                      <table class="table table-sm mb-0 inner">
                        <thead><tr><th>Item</th><th class="text-right">Requested</th><th class="text-right">Vetted</th><th class="text-right">Approved</th><th>Verdict</th></tr></thead>
                        <tbody>
                          <tr v-for="l in c.items" :key="l.id">
                            <td><span class="badge badge-light mr-1">{{ l.type }}</span>{{ l.name }}</td>
                            <td class="text-right">{{ money(l.cost) }}</td>
                            <td class="text-right">{{ l.vetted === null ? "—" : money(l.vetted) }}</td>
                            <td class="text-right">{{ l.approved === null ? "—" : money(l.approved) }}</td>
                            <td class="small">{{ l.remark || "" }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { getClaimStatus } from "@/utils/claimStatus";

/**
 * Claims by diagnosis, service, drug or programme with drill-down item → facilities → claims (GET claims/analysis),
 * on the Claims Statistics page. Uses the page's filters; reloads when refreshKey changes.
 */
export default {
  name: "ClaimAnalysis",
  props: {
    from: { type: String, default: "" },
    to: { type: String, default: "" },
    sector: { type: [Array, String], default: () => [] },
    providerId: { type: [Number, String], default: null },
    phcType: { type: String, default: "" },
    phcGeneral: { type: String, default: "" },
    refreshKey: { type: Number, default: 0 },
  },
  data() {
    return {
      dimensions: [
        { key: "diagnosis", label: "Diagnoses", singular: "Diagnosis", plural: "diagnoses" },
        { key: "service", label: "Services", singular: "Service", plural: "services" },
        { key: "drug", label: "Drugs", singular: "Drug", plural: "drugs" },
        { key: "programme", label: "Programmes", singular: "Programme", plural: "programmes" },
      ],
      dimension: "diagnosis",
      level: "items",
      item: null, // {key, name}
      facility: null, // {id, name}
      sort: "claimed",
      limit: 20,
      data: null,
      loading: false,
      exporting: false,
      error: "",
      chartKey: 0,
      open: {},
    };
  },
  computed: {
    currentDimension() { return this.dimensions.find((d) => d.key === this.dimension); },
    chartRows() { return this.data && this.data.rows ? this.data.rows.slice(0, 15) : []; },
    chartSeries() {
      if (this.sort === "claims") {
        return [
          { name: "Approved", data: this.chartRows.map((r) => r.approved_count) },
          { name: "Pending", data: this.chartRows.map((r) => r.pending_count) },
          { name: "Rejected", data: this.chartRows.map((r) => r.rejected_count) },
        ];
      }
      return [
        { name: "Approved", data: this.chartRows.map((r) => r.amount_approved) },
        { name: "Pending", data: this.chartRows.map((r) => r.amount_pending) },
        { name: "Rejected", data: this.chartRows.map((r) => r.amount_rejected) },
      ];
    },
    chartOptions() {
      const fmt = (v) => (this.sort === "claims" ? this.num(v) : this.money(v));
      return {
        chart: { stacked: true, toolbar: { show: true } },
        colors: ["#059669", "#d97706", "#dc2626"],
        plotOptions: { bar: { horizontal: true, barHeight: "75%" } },
        xaxis: { categories: this.chartRows.map((r) => (r.name.length > 40 ? r.name.slice(0, 38) + "…" : r.name)), labels: { formatter: fmt } },
        dataLabels: { enabled: false },
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false, y: { formatter: fmt } },
      };
    },
  },
  watch: {
    refreshKey() { this.load(); },
  },
  mounted() { this.load(); },
  methods: {
    params(extra = {}) {
      const sector = Array.isArray(this.sector) ? this.sector : this.sector ? [this.sector] : [];
      return {
        dimension: this.dimension, level: this.level, sort: this.sort, limit: this.limit,
        from: this.from || undefined, to: this.to || undefined, sector: sector.length ? sector : undefined,
        provider_id: this.providerId || undefined, phc_type: this.phcType || undefined, phc_general: this.phcGeneral || undefined,
        item: this.item ? (this.item.key === null ? "0" : this.item.key) : undefined,
        facility_id: this.facility ? this.facility.id : undefined,
        ...extra,
      };
    },
    load() {
      this.loading = true;
      this.error = "";
      this.axios.get("/api/v1/auth/claims/analysis", { params: this.params() })
        .then((res) => { this.data = res.data.data; this.open = {}; this.chartKey++; })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load the analysis."; })
        .finally(() => { this.loading = false; });
    },
    setDimension(key) {
      this.dimension = key;
      this.goTo("items");
    },
    goTo(level) {
      this.level = level;
      if (level === "items") this.item = null;
      if (level !== "claims") this.facility = null;
      this.data = null;
      this.load();
    },
    drill(row) {
      if (this.level === "items") {
        this.item = { key: row.key, name: row.name };
        this.goTo("facilities");
      } else if (this.level === "facilities" && row.facility_id) {
        this.facility = { id: row.facility_id, name: row.facility };
        this.goTo("claims");
      }
    },
    toggle(id) { this.$set(this.open, id, !this.open[id]); },
    statusLabel(status) { return getClaimStatus({ claim_status: status }).label; },
    statusClass(status) { return "badge-soft-" + getClaimStatus({ claim_status: status }).variant; },
    exportCsv() {
      this.exporting = true;
      this.axios.get("/api/v1/auth/claims/analysis", { params: this.params({ format: "csv", limit: 500 }), responseType: "blob" })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
          const a = document.createElement("a");
          a.href = url;
          a.download = `claims-by-${this.dimension}-${this.level}-${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => this.$toasted.error("Could not export.", { position: "top-center", duration: 3000 }))
        .finally(() => { this.exporting = false; });
    },
    num(v) { return Number(v || 0).toLocaleString(); },
    money(v) { return "₦" + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
.crumbs { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 0.85rem; }
.crumbs a { color: #2563eb; }
.crumbs .active { font-weight: 700; color: #1e293b; }
.crumbs i { color: #94a3b8; font-size: 0.75rem; }
.clickable { cursor: pointer; }
.item-row > td { background: #f8fafc; padding: 6px 16px 12px; }
.inner th { font-size: 0.72rem; color: #64748b; text-transform: uppercase; }
.badge-soft-success { background: #d1fae5; color: #065f46; }
.badge-soft-danger { background: #fee2e2; color: #991b1b; }
.badge-soft-warning { background: #fef3c7; color: #92400e; }
.badge-soft-info { background: #dbeafe; color: #1d4ed8; }
.badge-soft-dark { background: #e5e7eb; color: #1f2937; }
.badge-soft-indigo { background: #e0e7ff; color: #3730a3; }
.badge-soft-forwarded { background: #ede9fe; color: #5b21b6; }
.badge-soft-sent-back { background: #ffedd5; color: #9a3412; }
</style>
